import { FC } from 'react';
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline';
import Link from 'next/link';

import { YourWatch } from 'components/common/summary/YourWatch';
import { CurrencyInput } from 'components/design-system/CurrencyInput';
import { Slideout, SlideoutProps } from 'components/design-system/Slideout';
import { CommissionDetailsModal } from 'components/offers/CommissionDetailsModal';
import { useModal } from 'context/modal/ModalContext';
import { useAutoFocus } from 'hooks/useAutoFocus';
import { buildListingUrl } from 'lib/url_helpers';
import { Listing, SellerLevel, SellerProfile } from 'types';
import { formatPrice, getListingBreakdownPrice } from 'utils/text-helpers';

type ChangePriceSlideoutProps = {
  closeModal: () => void;
  isOpen?: boolean;
  listing: Listing;
  sellerProfile?: SellerProfile | null;
  sellerLevels?: SellerLevel[];
  title: string;
  submitButton: SlideoutProps['submit'];
  currentPrice?: number | string;
  setCurrentPrice: (price?: string) => void;
  inputErrorText?: string;
};

export const ChangePriceSlideout: FC<ChangePriceSlideoutProps> = ({
  closeModal,
  isOpen,
  listing,
  sellerProfile,
  sellerLevels,
  submitButton,
  title,
  currentPrice,
  setCurrentPrice,
  inputErrorText
}) => {
  const { autoFocus, enableAutoFocus } = useAutoFocus();
  const currentPriceCents = Number(currentPrice || 0) * 100;

  const { commissionCents, earnings, minimumTotalFee, sellerFeeCents } = getListingBreakdownPrice(
    {
      priceCents: currentPriceCents,
      overrideCommissionRateBips: listing.overrideCommissionRateBips,
      overrideSellerFeeCents: listing.overrideSellerFeeCents,
      overrideMinTotalFeeCents: listing.overrideMinTotalFeeCents
    },
    sellerProfile,
    sellerLevels
  );

  const { closeModal: closeCommissionModal, openModal } = useModal();

  const openCommissionModal = () => {
    openModal(
      <CommissionDetailsModal
        commission={commissionCents}
        minimalFee={minimumTotalFee}
        sellingPrice={listing.priceCents}
        earnings={earnings}
        sellerFee={sellerFeeCents}
        closeModal={closeCommissionModal}
      />,
      true
    );
  };

  const listingUrl = buildListingUrl(
    listing.model.brand.displayName,
    listing.model.displayName,
    listing.model.referenceNumber,
    listing.id
  );

  return (
    <Slideout onShow={enableAutoFocus} closeModal={closeModal} title={title} submit={submitButton} isOpen={isOpen}>
      <div className='riforma-regular text-center text-primary'>
        <div className='mb-5'>
          <CurrencyInput
            autoFocus={autoFocus}
            placeholder='Your price'
            className='w-100 fs-14px border rounded-2 py-3 px-4 border-success'
            prefix='$ '
            onValueChange={setCurrentPrice}
            value={currentPrice}
            isError={!!inputErrorText}
          />
          {inputErrorText && <span className='text-danger fs-12px'>{inputErrorText}</span>}
        </div>

        <div className='fs-14px px-2'>
          <Link href={listingUrl} target='_blank' className='text-start'>
            <YourWatch listing={listing} className='fs-14px mb-4 border-bottom pb-3' />
          </Link>
          <div className='d-flex w-100 justify-content-between mb-1'>
            <div>Selling price</div>
            <div>{formatPrice(currentPriceCents)}</div>
          </div>
          <div className='d-flex w-100 justify-content-between mb-1 opacity-60'>
            <div role='button' onClick={openCommissionModal} className='d-flex align-items-center'>
              Bezel Commission <MdInfoOutline className='ms-1' size={12} />
            </div>
            <div>{formatPrice(commissionCents)}</div>
          </div>
          <div className='d-flex justify-content-between fs-16px mb-4 riforma-medium'>
            <div>Earnings</div>
            <div className='text-success letter-spacing-005'>{formatPrice(earnings)}</div>
          </div>
        </div>
      </div>
    </Slideout>
  );
};
